import React, { ReactElement } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import App from './Containers/App/App';
import { Provider } from 'react-redux';
import store from './store';
import theme from './theme';
import { isMicroApp } from './Common/Common.utils';
import { externalAppInfoType } from './Common/Common.types';
import { get, includes } from 'lodash-es';
import qs from 'qs';
import './Pages/Flo/Flo.css';

// Tours (driver.js + ToursAPI) is a fairly heavy, opt-in feature that only
// activates when the embedding <script> tag has floik-tour="true". Capture
// the script tag synchronously (document.currentScript is only valid during
// this script's initial synchronous execution) and only pay for the Tours
// bundle when it's actually requested.
const toursScriptEl = document.currentScript as HTMLScriptElement | null;
if (toursScriptEl?.getAttribute('floik-tour') === 'true') {
	import('./Components/Tours/ToursGlobal').then(({ initToursGlobal }) => {
		initToursGlobal(toursScriptEl);
	});
} else {
	console.log('FloikTours: Not initialized - floik-tour attribute not set to true');
}

const isDev = process.env.REACT_APP_PUBLISH_ASSETS_BASE_URL === 'http://localhost:3000';

const cssFiles = {
	dev: 'main.b1d11c8b.css',
	qa: 'main.b1d11c8b.css',
};

const OLD_TAGS_ATTRIBUTES_LIST = [
	{
		queryParam: 'skip-video',
		attributeName: 'swift-play',
	},
	{
		queryParam: 'theme',
		attributeName: 'theme',
	},
	{
		queryParam: 'wrapper',
		attributeName: 'wrapper',
	},
	{
		queryParam: 'view',
		attributeName: 'view',
	},
	{
		queryParam: 'auto-play',
		attributeName: 'auto-play',
	},
	{
		queryParam: 'hide-header',
		attributeName: 'hide-header',
	},
	{
		queryParam: 'show-toc',
		attributeName: 'show-toc',
	},
	{
		queryParam: 'show-author',
		attributeName: 'show-author',
	},
	{
		queryParam: 'show-flo-title',
		attributeName: 'show-flo-title',
		default: 'true',
	},
	{
		queryParam: 'inApp',
		attributeName: 'inApp',
	},
];

let ErrorBoundary = ({ children }: { children: ReactElement }) => children;
// const PUBLISH_ASSETS_BASE_URL = 'http://localhost:4000/';
const PUBLISH_ASSETS_BASE_URL = `${process.env.REACT_APP_PUBLISH_ASSETS_BASE_URL}/`;
const DEPLOYMENT_ENV = process.env.REACT_APP_DEPLOYMENT_ENV || 'dev';
const CSS_FILE_PATH = `${PUBLISH_ASSETS_BASE_URL}static/css/${
	PUBLISH_ASSETS_BASE_URL.includes('realmofdev')
		? cssFiles[DEPLOYMENT_ENV]
		: 'main.b1d11c8b.css'
}`;

class FloikFlo extends HTMLElement {
	static observedAttributes = [];

	constructor() {
		// Always call super first in constructor
		super();
	}
}

if (!customElements.get('floik-flo')) {
	customElements.define('floik-flo', FloikFlo);
}

// The main CSS entry is emitted as main.[contenthash].css - a new hash on every
// build that changes styles. The hardcoded map above goes stale silently (new
// component styles render completely unstyled), so resolve the real filename
// from the build's asset-manifest.json and fall back to the map if that fails.
const resolveCssFilePath = async (): Promise<string> => {
	try {
		const manifest = await fetch(
			`${PUBLISH_ASSETS_BASE_URL}asset-manifest.json?_ts=${Date.now()}`
		).then((res) => res.json());
		const mainCss = manifest?.files?.['main.css'];
		if (mainCss) {
			const path = mainCss.startsWith('/') ? mainCss : `/${mainCss}`;
			return `${PUBLISH_ASSETS_BASE_URL.replace(/\/$/, '')}${path}`;
		}
	} catch (e) {
		console.warn('Floik: failed to resolve css from asset-manifest, using fallback', e);
	}
	return CSS_FILE_PATH;
};

const addStyleLink = async () => {
	const cssFilePath = await resolveCssFilePath();
	const isStyleAdded = document.querySelector(`[href="${cssFilePath}"]`);
	if (isStyleAdded) {
		return;
	}

	// For exe-v2 builds, remove any stale stylesheets from the old /exe build
	// so they do not conflict with the new styles in micro-app/embed mode.
	// Micro-app bundles remain in /exe, so this cleanup must only run for exe-v2.
	if (PUBLISH_ASSETS_BASE_URL.includes('/exe-v2/')) {
		document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
			const href = link.getAttribute('href') || '';
			if (href.includes('/exe/') && !href.includes('/exe-v2/')) {
				link.remove();
			}
		});
	}

	const linkElement = document.createElement('link');
	linkElement.rel = 'stylesheet';
	linkElement.href = cssFilePath;
	const isIndexCssPresent = document.querySelector(
		`[href="https://www.floik.com/exe-v2/index.css"]`
	);
	if (isIndexCssPresent) {
		document.head.append(linkElement);
	} else {
		document.head.prepend(linkElement);
	}
};

if (isMicroApp()) {
	addStyleLink();
}
window.floikPreviewInstance = {};

const renderApp = (
	element: Element | DocumentFragment,
	externalAppInfo?: externalAppInfoType
) => {
	const root = ReactDOMClient.createRoot(element);

	addStyleLink();

	if (isMicroApp()) {
		window.floikPreviewInstance.appRoot = {
			unmount: () => {
				root.unmount();
			},
		};
	}

	const scriptPath = PUBLISH_ASSETS_BASE_URL.includes('realmofdev')
		? `https://www.realmofdev.com/exe-v2/`
		: PUBLISH_ASSETS_BASE_URL;
	const urlPath = `${isDev ? '/' : scriptPath}main.js`;

	const scriptQuery = `script[src="${urlPath}"]`;
	const scriptTag = document.querySelector(scriptQuery);

	const floSpaceId = scriptTag?.getAttribute('id') || '';
	// const floSpaceId = "64c8ed1fdc0ab9016fff5a20";

	root.render(
		<ErrorBoundary>
			<ThemeProvider theme={theme}>
				<Provider store={store}>
					<App externalAppInfo={externalAppInfo} floSpaceId={floSpaceId} />
				</Provider>
			</ThemeProvider>
		</ErrorBoundary>
	);
};

const updateOldTags = () => {
	const oldEmbeddedElements = document.querySelectorAll('[data-f-id*=_]');
	if (oldEmbeddedElements.length > 0) {
		const queryParam = qs.parse(location.search.replace('?', ''));
		for (let i = 0; i < oldEmbeddedElements.length; i++) {
			const element = oldEmbeddedElements[i];
			const newEmbedElement = document.createElement('floik-flo');
			const attributeNames = element.getAttributeNames();
			for (let j = 0; j < attributeNames.length; j++) {
				if (attributeNames[j] === 'data-f-id') {
					const splitId = element.getAttribute('data-f-id')?.split('_');
					const scriptTag = document.querySelector(
						`script[src="${isDev ? '/' : PUBLISH_ASSETS_BASE_URL}main.js"]`
					);
					scriptTag?.setAttribute('id', get(splitId, '[0]', ''));
					newEmbedElement.setAttribute('flo-id', get(splitId, '[1]', ''));
					newEmbedElement.setAttribute('is-embed', includes(location.pathname, '/embed'));
					OLD_TAGS_ATTRIBUTES_LIST.forEach((attribute) => {
						newEmbedElement.setAttribute(
							attribute.attributeName,
							String(get(queryParam, attribute.queryParam, attribute.default || 'false'))
						);
					});
					if (element.getAttribute('data-micro-view') !== 'true')
						newEmbedElement.setAttribute('style', 'width: 100vw; height: 100vh');
				} else if (attributeNames[j] === 'data-micro-view') {
					newEmbedElement.setAttribute('doc360-app', 'true');
				} else {
					newEmbedElement.setAttribute(
						attributeNames[j],
						element.getAttribute(attributeNames[j]) || ''
					);
				}
			}
			element.parentNode?.replaceChild(newEmbedElement, element);
		}
	}
};

const renderMicroApp = (externalAppInfo?: externalAppInfoType) => {
	updateOldTags();
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements.length > 0) {
		const rootElement = document.createElement('div');
		rootElement.setAttribute('style', 'width: 0;height: 0;position:fixed;top:-9999px');
		document.body.append(rootElement);
		renderApp(rootElement, externalAppInfo);
	}
};

if (!isMicroApp()) {
	updateOldTags();
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[0]) {
		renderMicroApp();
	} else {
		let rootElement = document.getElementById('root') as Element | DocumentFragment;
		if (rootElement) {
			renderApp(rootElement);
		}
	}
}

window.floikPreviewInstance.renderMicroApp = renderMicroApp;

window.doc360PreviewInstance?.onMessageFromMicroApp({
	type: 'floik_script_loaded',
	payload: {
		loaded: true,
	},
});
